import React from 'react';
import { t, Trans } from '@lingui/macro';
import styled from 'styled-components';

import { StarRating } from '@rover/components/src/formFields';
import LOCAL_STORAGE_KEYS from '@rover/constants/src/search/localStorage.constants';
import { ServiceType } from '@rover/constants/src/translations/service.constants';
import { Box, Button, Flex, Heading, Image, Link, Text } from '@rover/kibble/core';
import type { HomepageRebookingProfile } from '@rover/rsdk/src/apiClient/latest/model/homepageRebookingProfile';
import fireCrossPageEvent from '@rover/utilities/fireCrossPageEvent';

import useInGroomingPilot from '../../../../grooming/hooks/useInGroomingPilot';
import useInTrainingPilot from '../../../../training/hooks/useInTrainingPilot';
import { RebookClickEvent } from '../../utilities/analyticsEvents';

export type Props = {
  sitters: HomepageRebookingProfile[];
  language?: string;
};

const DateText = styled(Text)`
  font-size: 12px;

  span {
    white-space: nowrap;
    font-size: 12px;
  }
`;

const dateFormatter = (startDateString, endDateString, language = 'en-us'): JSX.Element => {
  const startDate = new Date(startDateString.replace(/-/g, '/'));
  const endDate = new Date(endDateString.replace(/-/g, '/'));
  const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
  if (startDate.getTime() !== endDate.getTime()) {
    return (
      <DateText textColor="placeholder" mb="4x">
        <Trans comment="The first variable is the start date and the second one the end date of a booked service">
          Booked <Text>{startDate.toLocaleString(language, options)} -</Text>{' '}
          <Text>{endDate.toLocaleString(language, options)}</Text>
        </Trans>
      </DateText>
    );
  }
  return (
    <DateText textColor="placeholder" mb="4x">
      <Trans comment="The variable is the start date and the end date of a booked service">
        Booked{' '}
        <Text size="50" sx={{ whiteSpace: 'nowrap' }}>
          {startDate.toLocaleString(language, options)}
        </Text>
      </Trans>
    </DateText>
  );
};

const RebookingItems = ({ language, sitters = [] }: Props): JSX.Element => {
  const { shouldShowTrainingEntrypoints } = useInTrainingPilot();
  const { shouldShowGroomingEntrypoints } = useInGroomingPilot();

  const getProfileUrl = (
    serviceTypeSlug: string,
    profileUrl: string,
    trainerProfileUrl: string,
    groomerProfileUrl: string
  ): string => {
    if (serviceTypeSlug === ServiceType.DOG_TRAINING && shouldShowTrainingEntrypoints) {
      return trainerProfileUrl;
    }
    if (serviceTypeSlug === ServiceType.GROOMING && shouldShowGroomingEntrypoints) {
      return groomerProfileUrl;
    }
    return `${profileUrl}?service_type=${serviceTypeSlug}`;
  };

  const getRebookUrl = (
    serviceTypeSlug: string,
    contactUrl: string,
    trainerRebookUrl: string,
    groomerContactUrl: string
  ): string => {
    if (serviceTypeSlug === ServiceType.DOG_TRAINING && shouldShowTrainingEntrypoints) {
      return trainerRebookUrl;
    }
    if (serviceTypeSlug === ServiceType.GROOMING && shouldShowGroomingEntrypoints) {
      return groomerContactUrl;
    }
    return `${contactUrl}?service_type=${serviceTypeSlug}`;
  };

  const getRebookButtonLabel = (serviceTypeSlug: string): JSX.Element => {
    if (serviceTypeSlug === ServiceType.DOG_TRAINING) {
      return <Trans>Rebook trainer</Trans>;
    }
    if (serviceTypeSlug === ServiceType.GROOMING) {
      return <Trans>Rebook groomer</Trans>;
    }
    return <Trans>Rebook sitter</Trans>;
  };

  return (
    <Flex flexGrow="1" width="100%">
      {sitters.map(
        (
          {
            isRatable,
            name,
            profileUrl,
            imageUrl,
            startDate,
            endDate,
            showRating,
            rating,
            reviewLinkAnchorText,
            reviewUrl,
            contactUrl,
            serviceTypeSlug,
            opk,
            serviceTypeDisplayName,
            trainerRebookUrl,
            trainerProfileUrl,
            groomerProfileUrl,
            groomerContactUrl,
          },
          index
        ) => (
          <Flex
            mr={index === 0 && sitters.length > 1 ? '4x' : '0x'}
            key={`sitter-rebook-${index}`}
            flexDirection="column"
            justifyContent="space-between"
            width="100%"
            flexGrow="1"
            flexShrink="1"
          >
            <Flex
              aria-label={t`${name}'s Profile page`}
              onClick={() =>
                fireCrossPageEvent(LOCAL_STORAGE_KEYS.HOMEPAGE_REBOOK, {
                  event: 'rebook-provider-profile-click',
                  providerId: opk,
                  rank: (index + 1).toString(),
                })
              }
              flexDirection="column"
              width="100%"
              flexGrow="1"
              flexShrink="1"
              gap="3x"
              webAs="a"
              webAsProps={{
                href: getProfileUrl(
                  serviceTypeSlug,
                  profileUrl,
                  trainerProfileUrl,
                  groomerProfileUrl
                ),
              }}
              sx={{
                overflow: 'hidden',
                cursor: 'pointer',
                '&:hover, &:focus, &:visited': {
                  textDecoration: 'none !important',
                },
              }}
            >
              <Box flexGrow="1" flexShrink="1" position="relative" sx={{ aspectRatio: '1.8' }}>
                <Image
                  src={imageUrl}
                  height="100%"
                  width="100%"
                  description={name}
                  resizeMethod="cover"
                  borderRadius="primary"
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                  }}
                />
              </Box>

              <Heading textColor="primary">{name}</Heading>
            </Flex>

            <Text size="100">{serviceTypeDisplayName}</Text>
            {dateFormatter(startDate, endDate, language)}

            <Box minHeight="40px" mb="4x">
              {showRating && rating !== null ? (
                <>
                  <StarRating rating={rating} />
                  <Text textColor="placeholder" size="50">
                    <Trans>My review</Trans>
                  </Text>
                </>
              ) : (
                <Flex alignItems="center">
                  {isRatable && reviewLinkAnchorText && (
                    <Link size="100" href={reviewUrl}>
                      {reviewLinkAnchorText}
                    </Link>
                  )}
                </Flex>
              )}
            </Box>

            <Button
              href={getRebookUrl(serviceTypeSlug, contactUrl, trainerRebookUrl, groomerContactUrl)}
              fullWidth
              variant="primary"
              size="small"
              onPress={() =>
                new RebookClickEvent({
                  providerId: opk,
                  rank: (index + 1).toString(),
                  serviceType: serviceTypeSlug,
                }).fireCrossPageEvent()
              }
              aria-label={t`Rebook ${name}`}
              py="3x"
            >
              <Text size="200">{getRebookButtonLabel(serviceTypeSlug)}</Text>
            </Button>
          </Flex>
        )
      )}
    </Flex>
  );
};

export default RebookingItems;
